import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { io } from "socket.io-client";
import { 
  Trophy, 
  Timer, 
  CheckCircle2,
  XCircle,
  Loader2,
  Zap,
  Medal,
  Star,
  Target
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function GamePlayPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // State
  const [socket, setSocket] = useState(null);
  const [gameCode, setGameCode] = useState("");
  const [nickname, setNickname] = useState("");
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionNumber, setQuestionNumber] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [answerSubmitted, setAnswerSubmitted] = useState(false);
  const [answerResult, setAnswerResult] = useState(null);
  const [totalPoints, setTotalPoints] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [gameEnded, setGameEnded] = useState(false);
  const [finalResults, setFinalResults] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [simulationInput, setSimulationInput] = useState("");
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  // Initialize from session storage and connect socket
  useEffect(() => {
    const code = sessionStorage.getItem("game_code");
    const nick = sessionStorage.getItem("game_nickname");
    
    console.log("[PLAYER PLAY] Initializing with:", { code, nick });
    
    if (!code || !nick) {
      console.log("[PLAYER PLAY] Missing session data, redirecting to join");
      navigate("/game/join");
      return;
    }

    setGameCode(code);
    setNickname(nick);

    const newSocket = io(BACKEND_URL, {
      transports: ["websocket", "polling"],
      path: "/api/socket.io"
    });

    newSocket.on("connect", () => {
      console.log("[PLAYER PLAY] Connected to game server, socket ID:", newSocket.id);
      // Use player_rejoin to reconnect to active game (NOT player_join)
      console.log("[PLAYER PLAY] Sending player_rejoin for code:", code, "nickname:", nick);
      newSocket.emit("player_rejoin", {
        code: code,
        nickname: nick,
        user_id: null
      });
    });

    newSocket.on("disconnect", () => {
      console.log("[PLAYER PLAY] Disconnected from game server");
    });

    // Rejoin success - player is back in the game
    newSocket.on("rejoin:success", (data) => {
      console.log("[PLAYER PLAY] Rejoin successful:", data);
      setTotalQuestions(data.total_questions || 0);
      
      // If there's a current question, show it
      if (data.current_question) {
        console.log("[PLAYER PLAY] Restoring current question:", data.current_question);
        setCurrentQuestion(data.current_question.question);
        setQuestionNumber(data.current_question.question_number);
        setTotalQuestions(data.current_question.total_questions);
        startTimeRef.current = Date.now();
        
        // Start timer
        if (data.current_question.question.time_limit) {
          setTimeRemaining(data.current_question.question.time_limit);
        }
      }
    });

    // Rejoin error - redirect back to join page
    newSocket.on("rejoin:error", (data) => {
      console.log("[PLAYER PLAY] Rejoin error:", data);
      sessionStorage.removeItem("game_code");
      sessionStorage.removeItem("game_nickname");
      navigate("/game/join");
    });

    // Question shown event
    newSocket.on("question:show", (data) => {
      console.log("[PLAYER PLAY] Question received:", data);
      setCurrentQuestion(data.question);
      setQuestionNumber(data.question_number);
      setTotalQuestions(data.total_questions);
      setSelectedAnswer(null);
      setAnswerSubmitted(false);
      setAnswerResult(null);
      setShowResults(false);
      setSimulationInput("");
      
      // Start timer
      startTimeRef.current = Date.now();
      setTimeRemaining(data.question.time_limit);
      
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    });

    // Answer result event
    newSocket.on("answer:result", (data) => {
      console.log("[PLAYER] Answer result:", data);
      setAnswerResult(data);
      setTotalPoints(data.total_points);
      if (timerRef.current) clearInterval(timerRef.current);
    });

    // Results shown event
    newSocket.on("results:show", (data) => {
      console.log("[PLAYER] Results shown:", data);
      setShowResults(true);
      setLeaderboard(data.leaderboard || []);
      if (timerRef.current) clearInterval(timerRef.current);
    });

    // Game ended event
    newSocket.on("game:ended", (data) => {
      console.log("[PLAYER] Game ended:", data);
      setFinalResults(data);
      setGameEnded(true);
      if (timerRef.current) clearInterval(timerRef.current);
      // Clean up session storage
      sessionStorage.removeItem("game_code");
      sessionStorage.removeItem("game_nickname");
      sessionStorage.removeItem("game_socket_id");
    });

    setSocket(newSocket);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      newSocket.disconnect();
    };
  }, [navigate]);

  const submitAnswer = (answer) => {
    if (answerSubmitted || !socket || !currentQuestion) return;
    
    const responseTimeMs = Date.now() - startTimeRef.current;
    
    setSelectedAnswer(answer);
    setAnswerSubmitted(true);
    
    socket.emit("player_answer", {
      code: gameCode,
      answer: answer,
      response_time_ms: responseTimeMs
    });
  };

  const submitSimulation = () => {
    if (answerSubmitted || !socket || !simulationInput.trim()) return;
    submitAnswer(simulationInput.trim());
  };

  // Option colors
  const optionColors = {
    A: { bg: "bg-red-500", hover: "hover:bg-red-600", selected: "ring-red-300" },
    B: { bg: "bg-blue-500", hover: "hover:bg-blue-600", selected: "ring-blue-300" },
    C: { bg: "bg-green-500", hover: "hover:bg-green-600", selected: "ring-green-300" },
    D: { bg: "bg-yellow-500", hover: "hover:bg-yellow-600", selected: "ring-yellow-300" }
  };

  // Find player's rank in leaderboard
  const getMyRank = () => {
    if (!finalResults) return null;
    const myEntry = finalResults.final_leaderboard?.find(
      entry => entry.nickname === nickname
    );
    return myEntry;
  };

  // Waiting for question
  if (!currentQuestion && !gameEnded) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 to-purple-800 flex items-center justify-center">
        <div className="text-center text-white">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Get Ready!</h2>
          <p className="opacity-80">Waiting for the next question...</p>
          <div className="mt-6 bg-white/20 rounded-full px-4 py-2 inline-flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-300" />
            <span className="font-bold">{totalPoints} pts</span>
          </div>
        </div>
      </div>
    );
  }

  // Game ended screen
  if (gameEnded && finalResults) {
    const myResult = getMyRank();
    const isWinner = myResult?.rank === 1;
    const isTopThree = myResult?.rank <= 3;

    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="text-center text-white mb-8">
            <div className="text-6xl mb-4">
              {isWinner ? "🏆" : isTopThree ? "🎉" : "⭐"}
            </div>
            <h1 className="text-3xl font-bold mb-2">
              {isWinner ? "You Won!" : isTopThree ? "Amazing!" : "Great Job!"}
            </h1>
          </div>

          {myResult && (
            <div className="bg-white rounded-3xl p-8 shadow-2xl text-center mb-6">
              <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${
                myResult.rank === 1 ? "bg-yellow-500" :
                myResult.rank === 2 ? "bg-gray-400" :
                myResult.rank === 3 ? "bg-amber-600" :
                "bg-gray-300"
              }`}>
                {myResult.rank <= 3 ? (
                  <Medal className="w-10 h-10 text-white" />
                ) : (
                  <span className="text-2xl font-bold text-white">#{myResult.rank}</span>
                )}
              </div>
              
              <h2 className="text-2xl font-bold text-gray-900 mb-1">{nickname}</h2>
              <p className="text-gray-500 mb-4">
                {myResult.rank === 1 ? "1st Place" :
                 myResult.rank === 2 ? "2nd Place" :
                 myResult.rank === 3 ? "3rd Place" :
                 `${myResult.rank}th Place`}
              </p>
              
              <div className="bg-yellow-100 rounded-2xl p-4 mb-4">
                <div className="text-4xl font-bold text-yellow-600">{myResult.points}</div>
                <div className="text-sm text-yellow-700">Total Points</div>
              </div>

              {myResult.disc_type && (
                <div className="bg-purple-100 rounded-xl p-3">
                  <div className="text-sm text-purple-600 font-medium">Your DISC Bird</div>
                  <div className="text-lg font-bold text-purple-700">
                    {myResult.disc_type === "eagle" && "🦅 Eagle"}
                    {myResult.disc_type === "parrot" && "🦜 Parrot"}
                    {myResult.disc_type === "dove" && "🕊️ Dove"}
                    {myResult.disc_type === "owl" && "🦉 Owl"}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Top 3 display */}
          <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4">
            <h3 className="text-white font-bold text-center mb-3">🏆 Top Players</h3>
            {finalResults.final_leaderboard?.slice(0, 3).map((entry, idx) => (
              <div
                key={idx}
                className={`flex items-center justify-between p-3 rounded-xl mb-2 ${
                  entry.nickname === nickname ? "bg-white/40" : "bg-white/20"
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    idx === 0 ? "bg-yellow-500 text-white" :
                    idx === 1 ? "bg-gray-400 text-white" :
                    "bg-amber-600 text-white"
                  }`}>
                    {idx + 1}
                  </span>
                  <span className="font-semibold text-white">{entry.nickname}</span>
                </div>
                <span className="font-bold text-white">{entry.points} pts</span>
              </div>
            ))}
          </div>

          <div className="text-center mt-6">
            <button
              onClick={() => navigate("/")}
              className="bg-white hover:bg-gray-100 text-gray-800 px-6 py-3 rounded-full font-semibold transition-all"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Active question screen
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 to-purple-800">
      {/* Header */}
      <div className="bg-black/20 px-4 py-3">
        <div className="max-w-md mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="bg-white/20 px-3 py-1 rounded-full text-white text-sm font-semibold">
              Q{questionNumber}/{totalQuestions}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <div className="bg-yellow-500 px-3 py-1 rounded-full flex items-center gap-1">
              <Zap className="w-4 h-4 text-white" />
              <span className="font-bold text-white">{totalPoints}</span>
            </div>
            {timeRemaining > 0 && !answerSubmitted && (
              <div className={`px-3 py-1 rounded-full flex items-center gap-1 ${
                timeRemaining <= 5 ? "bg-red-500 animate-pulse" : "bg-white/20"
              }`}>
                <Timer className="w-4 h-4 text-white" />
                <span className="font-bold text-white">{timeRemaining}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 py-6 max-w-md mx-auto">
        {/* Question */}
        <div className="bg-white rounded-2xl p-6 mb-6 shadow-lg">
          <h2 className="text-lg font-bold text-gray-900 leading-tight">
            {currentQuestion.question}
          </h2>
        </div>

        {/* Answer Result Feedback */}
        {answerResult && (
          <div className={`mb-6 p-4 rounded-2xl text-center ${
            answerResult.correct === true ? "bg-green-500" :
            answerResult.correct === false ? "bg-red-500" :
            "bg-blue-500"
          }`}>
            <div className="text-3xl mb-2">
              {answerResult.correct === true && "🎉"}
              {answerResult.correct === false && "😅"}
              {answerResult.correct === null && "✓"}
            </div>
            <div className="text-white font-bold">
              {answerResult.correct === true && "Correct!"}
              {answerResult.correct === false && "Not quite!"}
              {answerResult.correct === null && "Answer recorded!"}
            </div>
            <div className="text-white/80 text-sm mt-1">
              +{answerResult.points} points
            </div>
          </div>
        )}

        {/* Quiz/Decision Options */}
        {currentQuestion.options && !showResults && (
          <div className="space-y-3">
            {currentQuestion.options.map((option) => {
              const colors = optionColors[option.id];
              const isSelected = selectedAnswer === option.id;
              const isCorrect = answerResult?.correct_answer === option.id;
              const isWrong = answerSubmitted && isSelected && answerResult?.correct === false;
              
              return (
                <button
                  key={option.id}
                  onClick={() => submitAnswer(option.id)}
                  disabled={answerSubmitted}
                  data-testid={`answer-${option.id}`}
                  className={`w-full p-5 rounded-2xl text-white font-semibold text-left transition-all ${
                    colors.bg
                  } ${
                    !answerSubmitted ? colors.hover : ""
                  } ${
                    isSelected ? `ring-4 ${colors.selected}` : ""
                  } ${
                    answerSubmitted && isCorrect ? "ring-4 ring-green-300" : ""
                  } disabled:opacity-75`}
                >
                  <div className="flex items-center gap-3">
                    <span className="bg-white/20 w-10 h-10 rounded-lg flex items-center justify-center text-xl font-bold">
                      {option.id}
                    </span>
                    <span className="flex-1">{option.text}</span>
                    {answerSubmitted && isCorrect && (
                      <CheckCircle2 className="w-6 h-6" />
                    )}
                    {isWrong && (
                      <XCircle className="w-6 h-6" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Simulation Input */}
        {currentQuestion.type === "simulation" && currentQuestion.simulation_config && !answerSubmitted && (
          <div className="bg-white rounded-2xl p-6 shadow-lg">
            {currentQuestion.simulation_config.type === "text_input" && (
              <>
                <input
                  type="text"
                  value={simulationInput}
                  onChange={(e) => setSimulationInput(e.target.value)}
                  placeholder={currentQuestion.simulation_config.placeholder}
                  maxLength={currentQuestion.simulation_config.max_length}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-lg mb-4"
                />
                <button
                  onClick={submitSimulation}
                  disabled={!simulationInput.trim()}
                  className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 text-white py-3 rounded-xl font-bold transition-all"
                >
                  Submit Answer
                </button>
              </>
            )}

            {currentQuestion.simulation_config.type === "slider" && (
              <>
                <div className="text-center mb-4">
                  <span className="text-4xl font-bold text-purple-600">
                    {simulationInput || currentQuestion.simulation_config.default}
                  </span>
                  <span className="text-gray-500 ml-2">
                    {currentQuestion.simulation_config.unit}
                  </span>
                </div>
                <input
                  type="range"
                  value={simulationInput || currentQuestion.simulation_config.default}
                  onChange={(e) => setSimulationInput(e.target.value)}
                  min={currentQuestion.simulation_config.min}
                  max={currentQuestion.simulation_config.max}
                  step={currentQuestion.simulation_config.step}
                  className="w-full mb-4"
                />
                <button
                  onClick={() => submitAnswer(simulationInput || currentQuestion.simulation_config.default)}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-xl font-bold transition-all"
                >
                  Submit Answer
                </button>
              </>
            )}
          </div>
        )}

        {/* Waiting message after answer */}
        {answerSubmitted && !showResults && (
          <div className="text-center text-white mt-6">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
            <p className="opacity-80">Waiting for other players...</p>
          </div>
        )}

        {/* Results/Leaderboard */}
        {showResults && (
          <div className="bg-white rounded-2xl p-6 shadow-lg">
            {/* Explanation */}
            {currentQuestion.explanation && (
              <div className="bg-green-50 rounded-xl p-4 mb-4">
                <p className="text-green-800 text-sm">
                  <strong>💡</strong> {currentQuestion.explanation}
                </p>
              </div>
            )}
            
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-500" />
              Current Standings
            </h3>
            <div className="space-y-2">
              {leaderboard.slice(0, 5).map((entry, idx) => {
                const isMe = entry.nickname === nickname;
                return (
                  <div
                    key={idx}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      isMe ? "bg-yellow-100 border-2 border-yellow-300" :
                      idx === 0 ? "bg-yellow-50" : "bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
                        idx === 0 ? "bg-yellow-500 text-white" :
                        idx === 1 ? "bg-gray-400 text-white" :
                        idx === 2 ? "bg-amber-600 text-white" :
                        "bg-gray-200 text-gray-600"
                      }`}>
                        {entry.rank}
                      </span>
                      <span className={`font-medium ${isMe ? "text-yellow-800" : "text-gray-700"}`}>
                        {entry.nickname} {isMe && "(You)"}
                      </span>
                    </div>
                    <span className="font-bold text-yellow-600">{entry.points}</span>
                  </div>
                );
              })}
            </div>
            
            <p className="text-center text-gray-500 text-sm mt-4">
              Waiting for next question...
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default GamePlayPage;
